/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Ring2All Team
 📄 File: SecurityActionMultiSelect.tsx
 🗓️ Created: 2025-12-02
 🧩 Description:
    Compact multi-select component for security log actions (single line)
────────────────────────────────────────────────────────────────*/

import { useState } from 'react';
import { Shield, ChevronDown } from 'lucide-react';
import { MultiSelectModal } from '../../../components/ui/MultiSelectModal';

interface SecurityActionMultiSelectProps {
  selectedActions: string[];
  onChange: (actions: string[]) => void;
  placeholder?: string;
}

// Available security log actions
const SECURITY_ACTIONS = [
  { id: 'LOGIN', name: 'LOGIN', description: 'User login events' },
  { id: 'LOGOUT', name: 'LOGOUT', description: 'User logout events' },
  { id: 'LOGIN_FAILED', name: 'LOGIN_FAILED', description: 'Failed login attempts' },
  { id: 'PASSWORD_CHANGE', name: 'PASSWORD_CHANGE', description: 'Password changes' },
  { id: 'PASSWORD_RESET', name: 'PASSWORD_RESET', description: 'Password resets' },
  { id: 'MFA_ENABLED', name: 'MFA_ENABLED', description: 'MFA enabled events' },
  { id: 'MFA_DISABLED', name: 'MFA_DISABLED', description: 'MFA disabled events' },
  { id: 'PERMISSION_CHANGE', name: 'PERMISSION_CHANGE', description: 'Permission changes' },
];

export function SecurityActionMultiSelect({
  selectedActions,
  onChange,
  placeholder = 'All Actions',
}: SecurityActionMultiSelectProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Get display text
  const getDisplayText = () => {
    if (selectedActions.length === 0) return placeholder;
    if (selectedActions.length === 1) return selectedActions[0];
    return `${selectedActions[0]} +${selectedActions.length - 1}`;
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setIsModalOpen(true)}
        className="flex items-center justify-between w-full h-9 px-3 py-2 text-sm border border-border rounded-md bg-background hover:border-primary/50 focus:border-primary focus:ring-1 focus:ring-primary/20 transition-colors"
      >
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <Shield className="h-4 w-4 text-muted-foreground flex-shrink-0" />
          <span className={`truncate ${selectedActions.length === 0 ? 'text-muted-foreground' : 'text-foreground'}`}>
            {getDisplayText()}
          </span>
        </div>
        <ChevronDown className="h-4 w-4 text-muted-foreground flex-shrink-0" />
      </button>

      <MultiSelectModal
        title="Select Security Actions"
        description="Choose the security events you want to filter by"
        items={SECURITY_ACTIONS}
        selectedIds={selectedActions}
        onSelectionChange={onChange}
        onClose={() => setIsModalOpen(false)}
        isOpen={isModalOpen}
        searchPlaceholder="Search actions..."
        icon={Shield}
        enableSelectAll
        selectAllLabel="Select All Actions"
      />
    </>
  );
}

